<template>
  <b-modal
    id="daily-department-sales-modal"
    size="lg"
    title="Daily Department Sales Report"
    @show="fetchReport"
    hide-footer
  >
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="secondary"></b-spinner>
      <p class="text-muted mt-3">Loading report...</p>
    </div>

    <div v-else-if="error" class="alert alert-danger">
      {{ error }}
    </div>

    <div v-else-if="reportData">
      <div class="mb-3 d-print-none">
        <b-button variant="primary" size="sm" @click="printThermal">
          <i class="bi bi-printer"></i> Print Thermal
        </b-button>
      </div>

      <div id="thermal-print-area">
        <div class="receipt-header">
          <div class="company-name">{{ reportData.company_name || 'N/A' }}</div>
          <div class="report-title">Sales Report</div>
          <div class="report-date">{{ reportData.report && reportData.report.report_date ? formatDate(reportData.report.report_date) : '-' }}</div>
        </div>

        <div class="line-separator">================================</div>

        <div v-for="(dept, deptIndex) in (reportData.report && reportData.report.departments ? reportData.report.departments : [])"
             :key="`dept-${deptIndex}`"
             class="department-section">
          <div class="section-header">{{ dept.name || 'Uncategorized' }}</div>

          <div v-for="(item, itemIndex) in (dept.items || [])"
               :key="`item-${deptIndex}-${itemIndex}`"
               class="item-row">
            <div class="item-name-qty">
              <span class="item-name">{{ item.name || '-' }}</span>
              <span class="item-qty">x{{ item.quantity || 0 }}</span>
            </div>
          </div>

          <div class="section-subtotal">
            <div class="subtotal-row">
              <span>Total Income</span>
              <span class="amount-value">{{ formatCurrency(dept.total_revenue || 0) }}</span>
            </div>
            <div v-for="(amount, method) in (dept.payment_methods || {})"
                 :key="`dept-${deptIndex}-${method}`"
                 class="payment-row">
              <span class="payment-method">{{ method }}</span>
              <span class="amount-value">{{ formatCurrency(amount) }}</span>
            </div>
          </div>

          <div class="line-separator">--------------------------------</div>
        </div>

        <div class="grand-total-section">
          <div class="grand-total-row">
            <span class="grand-label">GRAND TOTAL</span>
            <span class="grand-amount">{{ formatCurrency(reportData.report && reportData.report.grand_total ? reportData.report.grand_total : 0) }}</span>
          </div>
          <div v-for="(amount, method) in (reportData.report && reportData.report.grand_payment_methods ? reportData.report.grand_payment_methods : {})"
               :key="`grand-${method}`"
               class="grand-payment-row">
            <span class="payment-method">{{ method }} Total</span>
            <span class="amount-value">{{ formatCurrency(amount) }}</span>
          </div>
        </div>

        <div class="line-separator">================================</div>

        <div class="receipt-footer">
          {{ new Date().toLocaleString() }}
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
export default {
  name: 'DailyDepartmentSalesModal',
  props: {
    selectedDate: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      reportData: null
    };
  },
  methods: {
    async fetchReport() {
      this.loading = true;
      this.error = null;
      this.reportData = null;

      try {
        const response = await axios.get('/api/sales-analytics/daily-department-sales', {
          params: {
            date: this.selectedDate
          }
        });

        this.reportData = response.data;
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to load report';
      } finally {
        this.loading = false;
      }
    },
    printThermal() {
      window.print();
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
#thermal-print-area {
  font-family: Arial, Helvetica, sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: #000;
  background: #fff;
  padding: 20px;
  max-width: 100%;
}

.receipt-header {
  text-align: center;
  margin-bottom: 16px;
}

.company-name {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 4px;
  text-transform: uppercase;
}

.report-title {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 4px;
}

.report-date {
  font-size: 13px;
  margin-bottom: 8px;
}

.line-separator {
  font-size: 14px;
  margin: 12px 0;
  color: #000;
  letter-spacing: 0;
}

.department-section {
  margin-bottom: 16px;
  page-break-inside: avoid;
}

.section-header {
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.item-row {
  margin-bottom: 2px;
}

.item-name-qty {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  padding: 2px 0;
}

.item-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: 8px;
}

.item-qty {
  font-weight: bold;
  white-space: nowrap;
}

.section-subtotal {
  margin-top: 12px;
  padding-top: 8px;
}

.subtotal-row {
  display: flex;
  justify-content: space-between;
  font-size: 15px;
  font-weight: bold;
  margin-bottom: 6px;
}

.payment-row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  padding: 2px 0;
  padding-left: 16px;
}

.payment-method {
  flex: 1;
}

.amount-value {
  font-weight: bold;
  white-space: nowrap;
  margin-left: 8px;
  text-align: right;
  min-width: 80px;
}

.grand-total-section {
  margin-top: 16px;
  margin-bottom: 16px;
  page-break-inside: avoid;
}

.grand-total-row {
  display: flex;
  justify-content: space-between;
  font-size: 17px;
  font-weight: bold;
  margin-bottom: 8px;
  padding: 4px 0;
}

.grand-label {
  flex: 1;
}

.grand-amount {
  font-weight: bold;
  white-space: nowrap;
  margin-left: 8px;
  text-align: right;
  min-width: 80px;
}

.grand-payment-row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  font-weight: bold;
  padding: 2px 0;
}

.receipt-footer {
  text-align: center;
  font-size: 11px;
  margin-top: 12px;
  color: #333;
}

@media print {
  @page {
    size: 80mm auto;
    margin: 2mm;
  }

  body * {
    visibility: hidden;
  }

  #thermal-print-area,
  #thermal-print-area * {
    visibility: visible;
  }

  #thermal-print-area {
    position: absolute;
    left: 0;
    top: 0;
    width: 80mm;
    margin: 0;
    padding: 3mm;
    background: #fff;
    font-family: Arial, Helvetica, sans-serif;
    font-size: 14pt;
    line-height: 1.3;
    font-weight: normal;
    color: #000;
  }

  .company-name {
    font-size: 20pt;
    font-weight: 900;
  }

  .report-title {
    font-size: 18pt;
    font-weight: 900;
  }

  .report-date {
    font-size: 14pt;
    font-weight: normal;
  }

  .section-header {
    font-size: 16pt;
    font-weight: 900;
  }

  .item-name-qty {
    font-size: 13pt;
    font-weight: normal;
  }

  .item-qty {
    font-weight: 900;
  }

  .subtotal-row {
    font-size: 14pt;
    font-weight: 900;
  }

  .payment-row {
    font-size: 13pt;
    font-weight: normal;
  }

  .payment-row .amount-value {
    font-weight: 900;
  }

  .grand-total-row {
    font-size: 18pt;
    font-weight: 900;
  }

  .grand-payment-row {
    font-size: 15pt;
    font-weight: 900;
  }

  .line-separator {
    font-size: 12pt;
    font-weight: normal;
  }

  .receipt-footer {
    font-size: 10pt;
    font-weight: normal;
  }

  .d-print-none {
    display: none !important;
  }

  .department-section {
    page-break-inside: avoid;
  }

  .grand-total-section {
    page-break-inside: avoid;
  }
}
</style>
